import { useEffect, useState } from "react";
import { Plus, Trash2, Save, GripVertical } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { FileUpload } from "@/components/admin/FileUpload";

const input = "w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]";

export function LinksManager() {
  const [site, setSite] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  useEffect(() => {
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
  }, []);

  if (!site) return <p className="text-[#555555]">Loading…</p>;

  const set = (k, v) => setSite((s) => ({ ...s, [k]: v }));
  const links = site.links || [];
  const add = () => set("links", [...links, { id: crypto.randomUUID(), title: "", url: "", description: "", image: "", pdf: "" }]);
  const upd = (i, k, v) => set("links", links.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const rm = (i) => set("links", links.filter((_, idx) => idx !== i));

  const onDrop = (i) => {
    if (dragIdx === null || dragIdx === i) {
      setDragIdx(null);
      setOverIdx(null);
      return;
    }
    const arr = [...links];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(i, 0, moved);
    set("links", arr);
    setDragIdx(null);
    setOverIdx(null);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/site", site);
      toast.success("Links saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="links-manager">
      <div className="flex justify-end">
        <button onClick={save} disabled={busy} data-testid="save-links-top-btn" className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-6 py-3 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
          <Save className="h-5 w-5" /> {busy ? "Saving…" : "Save links"}
        </button>
      </div>
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
        <label className="block text-sm font-medium text-[#333333] mb-1">Intro text</label>
        <textarea className={input} rows={2} value={site.links_intro || ""} onChange={(e) => set("links_intro", e.target.value)} data-testid="links-intro-input" />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading font-bold text-lg">Links</h3>
          <p className="text-xs text-[#555555]">Drag the handle to reorder. Order is saved with the page.</p>
        </div>
        <button onClick={add} data-testid="add-link-btn" className="inline-flex items-center gap-1 bg-[#E11D2A] text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-[#c41320]">
          <Plus className="h-4 w-4" /> Add link
        </button>
      </div>

      <div className="space-y-3">
        {links.map((l, i) => (
          <div
            key={l.id || i}
            onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
            onDrop={() => onDrop(i)}
            data-testid={`link-row-${i}`}
            className={`flex items-start gap-3 bg-white rounded-lg border p-4 transition-all ${
              overIdx === i && dragIdx !== null && dragIdx !== i ? "border-[#1E63C4] ring-2 ring-[#1E63C4]/30" : "border-[#E5E7EB]"
            } ${dragIdx === i ? "opacity-50" : ""}`}
          >
            <button
              draggable
              onDragStart={() => setDragIdx(i)}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              data-testid={`link-drag-${i}`}
              className="mt-1 cursor-grab active:cursor-grabbing text-[#999999] hover:text-[#1E63C4]"
              aria-label="Drag to reorder"
            >
              <GripVertical className="h-5 w-5" />
            </button>
            <div className="flex-1 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-[#555555]">Link {i + 1}</span>
                <button onClick={() => rm(i)} className="text-red-500" data-testid={`remove-link-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              <input className={input} placeholder="Title" value={l.title} onChange={(e) => upd(i, "title", e.target.value)} data-testid={`link-title-${i}`} />
              <input className={input} placeholder="https://… (ignored if a PDF is uploaded)" value={l.url} onChange={(e) => upd(i, "url", e.target.value)} data-testid={`link-url-${i}`} />
              <textarea className={input} rows={2} placeholder="Description (optional)" value={l.description} onChange={(e) => upd(i, "description", e.target.value)} data-testid={`link-desc-${i}`} />
              <ImageUpload label="Icon / image (optional)" value={l.image} onChange={(v) => upd(i, "image", v)} testid={`link-image-${i}`} />
              <FileUpload label="PDF document (optional)" value={l.pdf} onChange={(v) => upd(i, "pdf", v)} testid={`link-pdf-${i}`} />
              {l.pdf && <p className="text-xs text-[#1E63C4]">When a PDF is uploaded, clicking this link opens the PDF viewer instead of the URL.</p>}
            </div>
          </div>
        ))}
      </div>

      <button onClick={save} disabled={busy} data-testid="save-links-btn" className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-6 py-3 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
        <Save className="h-5 w-5" /> {busy ? "Saving…" : "Save links"}
      </button>
    </div>
  );
}

